import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetEngagementAnalyticsQuery } from "graphql/__generated__/hooks";
import { OrgCountBarChartItem } from "components/Charts/OrgCountBarChart";

export type EngagementSummary = {
  totalUsers: number;
  activeUsers: number;
  activeUserRate: string;
  dau: number;
  wau: number;
  mau: number;
  sessionsInPeriod: number;
};

function percentage(num: number, den: number): string {
  return den > 0 ? `${Math.round((num / den) * 100)}%` : "-";
}

function countDistinctUsers(
  rows: Array<{ user_id: string; activity_date: string }>,
  sinceDate: string,
): number {
  const ids = new Set(
    rows
      .filter((row) => !dayjs(row.activity_date).isBefore(sinceDate, "day"))
      .map((row) => row.user_id),
  );
  return ids.size;
}

export function useEngagementAnalytics(orgIds: string[], sinceDate: string) {
  const mauSince = dayjs().subtract(30, "day").format("YYYY-MM-DD");
  const wauSince = dayjs().subtract(7, "day").format("YYYY-MM-DD");
  const dauSince = dayjs().format("YYYY-MM-DD");
  const activitySince = dayjs(sinceDate).isBefore(mauSince, "day")
    ? dayjs(sinceDate).format("YYYY-MM-DD")
    : mauSince;

  const { data, loading, error } = useGetEngagementAnalyticsQuery({
    variables: {
      orgIds,
      sinceDate,
      activitySince,
    },
    skip: orgIds.length === 0,
  });

  const summary = useMemo<EngagementSummary>(() => {
    const users = data?.users || [];
    const activityDays = data?.activity_days || [];
    const activityInPeriod = activityDays.filter(
      (day) => !dayjs(day.activity_date).isBefore(sinceDate, "day"),
    );
    const activeUsers = new Set(activityInPeriod.map((day) => day.user_id)).size;
    const sessionsInPeriod = activityInPeriod
      .reduce((sum, day) => sum + (day.session_count || 0), 0);

    return {
      totalUsers: users.length,
      activeUsers,
      activeUserRate: percentage(activeUsers, users.length),
      dau: countDistinctUsers(activityDays, dauSince),
      wau: countDistinctUsers(activityDays, wauSince),
      mau: countDistinctUsers(activityDays, mauSince),
      sessionsInPeriod,
    };
  }, [data, dauSince, mauSince, sinceDate, wauSince]);

  const sessionsByOrg = useMemo<OrgCountBarChartItem[]>(() => {
    const organizations = data?.organization || [];
    const activityDays = data?.activity_days || [];
    return organizations.map((organization) => {
      const sessions = activityDays
        .filter(
          (day) =>
            day.user.org_id === organization.org_id &&
            !dayjs(day.activity_date).isBefore(sinceDate, "day"),
        )
        .reduce((sum, day) => sum + (day.session_count || 0), 0);
      return {
        org: organization.name,
        count: sessions,
      };
    });
  }, [data, sinceDate]);

  const recentSessions = data?.recent_sessions || [];

  return {
    summary,
    sessionsByOrg,
    recentSessions,
    loading,
    error,
  };
}
